import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, TextField } from "@mui/material"
import { StandardIdFilter } from "../../../../../shared/components/filters/components/StandardIdFilter"
import { StandardCustomerSelect } from "../../../../../shared/components/filters/components/StandardCustomerSelect"
import { StandardEmailFilter } from "../../../../../shared/components/filters/components/StandardEmailFilter"
import { CustomFilterMenuAccordion, FilterMenu } from "../../../../../shared/components/filters/FilterMenu"
import useFilterAccordions from "../../../../../shared/components/filters/hooks/useFilterAccordions"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDebouncedEffect } from "../../../../../shared/hooks/useDebouncedEffect"
import useActiveFiltersCount from "../hooks/useActiveFiltersCount"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { iterateStringEnum } from "../../../../../lib/enums"
import { CardType } from "../idCardsTypes"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { UNFINISHED_FILTER_ENVIRONMENTS } from "../../../../../shared/components/filters/constants/filterMenuConst"

export type FiltersDrawerIdCardsProps = Readonly<{
    cardType: string
    isInActive: boolean
    cardNumber: string
    email?: string
    employeeId?: string
    customerId?: string
    customerGroupId?: string
}>

export const defaultFilterOptionsList: FiltersDrawerIdCardsProps = {
    cardType: "",
    cardNumber: "",
    isInActive: false,
    email: "",
    employeeId: "",
    customerId: "",
    customerGroupId: "",
}

type FiltersDrawerContentIdCardsProps = {
    drawerOpen: boolean
    setDrawerOpen: (val: boolean) => void
    setFiltersList: Dispatch<SetStateAction<FiltersDrawerIdCardsProps>>
    filtersList: FiltersDrawerIdCardsProps
}

const useStyles = makeStyles()((theme) => ({
    input: {
        width: "20rem",
    },
    switch: {
        marginLeft: 0,
        width: "100%",
        color: theme.palette.text.primary,
        "& .MuiFormControlLabel-label": {
            width: "100%",
        },
    },
}))

type FilterAccordions = {
    card: boolean
    debtor: boolean
    user: boolean
}

export function FiltersDrawerIdCards({
    drawerOpen,
    setDrawerOpen,
    setFiltersList,
    filtersList,
}: FiltersDrawerContentIdCardsProps) {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const [optionsList, setOptionsList] = useState<FiltersDrawerIdCardsProps>(filtersList)

    const { expandedAccordions, toggleAccordion } = useFilterAccordions<FilterAccordions>({
        card: true,
        debtor: false,
        user: false,
    })

    const { cardActive, debtorActive, userActive } = useActiveFiltersCount(optionsList)
    // card type, e-mail and employee id are not accepted by the userCards endpoint yet
    const showClientSideFilters = useMatchEnvironment(UNFINISHED_FILTER_ENVIRONMENTS)
    useEffect(() => {
        setOptionsList(filtersList)
    }, [filtersList])

    useDebouncedEffect(() => {
        setFiltersList(optionsList)
    }, [optionsList, setFiltersList])

    const handleClose = () => {
        setDrawerOpen(false)
    }

    const handleClearAll = () => {
        setOptionsList({
            ...defaultFilterOptionsList,
        })
    }

    const handelClearByType = (type: keyof FiltersDrawerIdCardsProps) => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                [type]: defaultFilterOptionsList[type],
            }
        })
    }

    return (
        <FilterMenu
            drawerOpen={drawerOpen}
            handleClose={handleClose}
            handleClearAll={handleClearAll}
            isShowClearAllButton={!!cardActive || !!debtorActive || !!userActive}
        >
            <LocalizedStrict id="id-cars-filter-card-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    expanded={expandedAccordions.card}
                    toggleAccordion={() => toggleAccordion("card")}
                    title="Order"
                    handleClear={() => {
                        handelClearByType("cardType")
                        handelClearByType("cardNumber")
                        handelClearByType("isInActive")
                    }}
                    counter={cardActive}
                >
                    <Grid container direction="column" gap="1rem">
                        {showClientSideFilters && (
                            <FormControl fullWidth>
                                <LocalizedStrict id="merchant-id-cards-table-card-type">
                                    <InputLabel id="id-cards-table-card-type-select-label">Card Type</InputLabel>
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="merchant-id-cards-table-card-type-select-label"
                                    attrs={{ label: true }}
                                >
                                    <Select
                                        labelId="id-cards-table-card-type-select-label"
                                        id="id-cards-table-card-type-select"
                                        value={optionsList.cardType}
                                        label="Card Type"
                                        onChange={(e) =>
                                            setOptionsList((prev) => ({
                                                ...prev,
                                                cardType: e.target.value,
                                            }))
                                        }
                                        className={classes.input}
                                    >
                                        {[...iterateStringEnum(CardType)].map((value) => (
                                            <MenuItem key={value} value={value}>
                                                <LocalizedEnum
                                                    base="merchant-id-cards-table-card-type-select-item"
                                                    enumValue={value}
                                                >
                                                    <span>{value}</span>
                                                </LocalizedEnum>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </LocalizedStrict>
                            </FormControl>
                        )}
                        <LocalizedStrict
                            id="id-cars-filter-card-number-label"
                            attrs={{ label: true, placeholder: true }}
                        >
                            <TextField
                                name="orderId"
                                label="Order ID"
                                placeholder="Search by card number"
                                value={optionsList.cardNumber}
                                onChange={(e) => {
                                    const cardNumber = e.target.value
                                    setOptionsList((prev) => ({ ...prev, cardNumber }))
                                }}
                                fullWidth
                            />
                        </LocalizedStrict>
                        <LocalizedStrict id="id-cars-filter-switch-label" attrs={{ label: true }}>
                            <FormControlLabel
                                label="Include inactive cards"
                                control={
                                    <Switch
                                        checked={optionsList.isInActive}
                                        onChange={(_, state) =>
                                            setOptionsList((prev) => ({
                                                ...prev,
                                                isInActive: state,
                                            }))
                                        }
                                    />
                                }
                                labelPlacement="start"
                                className={classes.switch}
                            />
                        </LocalizedStrict>
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="id-cars-filter-debtor-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    expanded={expandedAccordions.debtor}
                    toggleAccordion={() => toggleAccordion("debtor")}
                    title="Debtor"
                    handleClear={() => {
                        handelClearByType("customerId")
                        handelClearByType("customerGroupId")
                    }}
                    counter={debtorActive}
                >
                    <StandardCustomerSelect
                        setCustomerId={(customerId) =>
                            setOptionsList((prev) => ({
                                ...prev,
                                customerId,
                            }))
                        }
                        setCustomerGroupId={(customerGroupId) =>
                            setOptionsList((prev) => ({
                                ...prev,
                                customerGroupId,
                            }))
                        }
                        customerId={optionsList.customerId}
                        customerGroupId={optionsList.customerGroupId}
                        isFullWidth
                        isFilterDrawer
                    />
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            {showClientSideFilters && (
                <LocalizedStrict id="id-cars-filter-end-user-title" attrs={{ title: true }}>
                    <CustomFilterMenuAccordion
                        expanded={expandedAccordions.user}
                        toggleAccordion={() => toggleAccordion("user")}
                        title="End user"
                        handleClear={() => {
                            handelClearByType("email")
                            handelClearByType("employeeId")
                        }}
                        counter={userActive}
                    >
                        <Grid container direction="column" gap="1rem">
                            <StandardEmailFilter
                                value={optionsList.email}
                                onChange={(email) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        email,
                                    }))
                                }}
                            />
                            <StandardIdFilter
                                value={optionsList.employeeId}
                                onChange={(employeeId) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        employeeId,
                                    }))
                                }}
                                type="employeeId"
                            />
                        </Grid>
                    </CustomFilterMenuAccordion>
                </LocalizedStrict>
            )}
        </FilterMenu>
    )
}
